import {createSelector} from 'reselect';
import type {FormSettingsState, LoadingState} from './store';
import type {
  FormSettingsPayload,
  FormSettingsError,
} from '@packages/systems/designer/forms/types';
import {NETWORK_STATUSES} from './store';
import type {DesignerStores} from '@packages/systems/designer/flux';

/** Base selector to get the form settings state */
const getFormSettingsState = (state: DesignerStores): FormSettingsState =>
  state.FormSettingsStore;

/** Helper to create entity key for consistent keying strategy */
const createEntityKey = (elementId: string, componentId?: string): string =>
  componentId ? `${elementId}:${componentId}` : elementId;

/**
 * Selector to get form settings for a specific form/component
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns FormSettingsPayload or null if not found
 */
export const getFormSettings = createSelector(
  getFormSettingsState,
  (_: DesignerStores, elementId: string, componentId?: string) =>
    createEntityKey(elementId, componentId),
  (state: FormSettingsState, key: string): FormSettingsPayload | null => {
    return state.entities[key] || null;
  }
);

/**
 * Selector to get fetching state for a specific form/component
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns LoadingState or default loading state
 */
export const getFormSettingsFetchingState = createSelector(
  getFormSettingsState,
  (_: DesignerStores, elementId: string, componentId?: string) =>
    createEntityKey(elementId, componentId),
  (state: FormSettingsState, key: string): LoadingState => {
    return state.fetchingStates[key] || NETWORK_STATUSES.INIT;
  }
);

/**
 * Selector to get updating state for a specific form/component
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns LoadingState or default loading state
 */
export const getFormSettingsUpdatingState = createSelector(
  getFormSettingsState,
  (_: DesignerStores, elementId: string, componentId?: string) =>
    createEntityKey(elementId, componentId),
  (state: FormSettingsState, key: string): LoadingState => {
    return state.updatingStates[key] || NETWORK_STATUSES.INIT;
  }
);

/**
 * Selector to check if form settings are currently being fetched
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns boolean indicating if fetching
 */
export const isFormSettingsFetching = createSelector(
  getFormSettingsFetchingState,
  (fetchingState: LoadingState): boolean =>
    fetchingState === NETWORK_STATUSES.LOADING
);

/**
 * Selector to check if form settings are currently being updated
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns boolean indicating if updating
 */
export const isFormSettingsUpdating = createSelector(
  getFormSettingsUpdatingState,
  (updatingState: LoadingState): boolean =>
    updatingState === NETWORK_STATUSES.LOADING
);

/**
 * Selector to get error state for a specific form/component
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns FormSettingsError or null if no error
 */
export const getFormSettingsError = createSelector(
  getFormSettingsState,
  (_: DesignerStores, elementId: string, componentId?: string) =>
    createEntityKey(elementId, componentId),
  (state: FormSettingsState, key: string): FormSettingsError | null => {
    return state.errors[key] || null;
  }
);

/**
 * Selector to check if form settings have an error
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns boolean indicating if there's an error
 */
export const hasFormSettingsError = createSelector(
  getFormSettingsError,
  (error: FormSettingsError | null): boolean => error !== null
);

/**
 * Selector to get UI state for a specific form/component
 * @param elementId - The form ID
 * @param componentId - Optional component ID
 * @returns UI state object with configuration flags
 */
export const getFormSettingsUiState = createSelector(
  getFormSettingsState,
  (_: DesignerStores, elementId: string, componentId?: string) =>
    createEntityKey(elementId, componentId),
  (state: FormSettingsState, key: string) => {
    return (
      state.uiStates[key] || {
        isWebflowConfigured: false,
        isEmailConfigured: false,
      }
    );
  }
);
